import React, { useEffect, useRef, useState } from "react";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const getFileName = (file) => {
  if (file instanceof File) {
    return file.name;
  }

  if (typeof file === "string") {
    const cleanPath = file.split("?")[0];
    const encodedName = cleanPath.split("/").pop() || "Dosya";

    try {
      return decodeURIComponent(encodedName);
    } catch {
      return encodedName;
    }
  }

  return "Dosya";
};

const getFileExtension = (file) => {
  const fileName = getFileName(file);
  const parts = fileName.split(".");

  return parts.length > 1
    ? parts.pop().toUpperCase()
    : "DOSYA";
};

const getExistingFileUrl = (path) => {
  if (!path || typeof path !== "string") {
    return "";
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const cleanBase = API_BASE.replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  return `${cleanBase}${cleanPath}`;
};

const FileItem = ({ file, onRemove }) => {
  const [fileUrl, setFileUrl] = useState("");

  useEffect(() => {
    if (file instanceof File) {
      const objectUrl = URL.createObjectURL(file);
      setFileUrl(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }

    setFileUrl(getExistingFileUrl(file));
  }, [file]);

  const fileName = getFileName(file);
  const extension = getFileExtension(file);

  return (
    <div className="fdz-file-item">
      <a
        href={fileUrl}
        download={fileName}
        target={file instanceof File ? undefined : "_blank"}
        rel={file instanceof File ? undefined : "noopener noreferrer"}
        className="fdz-file-link"
        title="Dosyayı indir"
        onClick={(event) => event.stopPropagation()}
      >
        <span className="fdz-file-icon">
          <span className="fdz-file-extension">{extension}</span>
        </span>

        <span className="fdz-file-information">
          <strong className="fdz-file-name">{fileName}</strong>

          <small className="fdz-file-description">
            {file instanceof File
              ? `${(file.size / 1024 / 1024).toFixed(2)} MB · İndirmek için tıklayın`
              : "Yüklü dosya · İndirmek için tıklayın"}
          </small>
        </span>
      </a>

      <button
        type="button"
        className="fdz-remove-button"
        title="Dosyayı kaldır"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onRemove();
        }}
      >
        ×
      </button>
    </div>
  );
};

const FileDropzone = ({
  value,
  onChange,
  multiple = false,
  required = false,
  accept = ".pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt,.csv",
}) => {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const files = multiple
    ? Array.isArray(value)
      ? value.filter(Boolean)
      : value
        ? [value]
        : []
    : value
      ? [value]
      : [];

  const openFilePicker = () => {
    inputRef.current?.click();
  };

  const addFiles = (selectedFiles) => {
    const selected = Array.from(selectedFiles || []);

    if (selected.length === 0) {
      return;
    }

    if (multiple) {
      onChange([...files, ...selected]);
    } else {
      onChange(selected[0]);
    }

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const removeFile = (index) => {
    if (multiple) {
      onChange(files.filter((_, fileIndex) => fileIndex !== index));
    } else {
      onChange("");
    }

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const preventDefault = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <>
      <style>
        {`
          .fdz-container {
            width: 100%;
          }

          .fdz-drop-area {
            position: relative;
            width: 100%;
            min-height: 155px;
            padding: 24px;
            border: 2px dashed #cfd5df;
            border-radius: 10px;
            background: #fafbfc;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            box-sizing: border-box;
            transition:
              border-color 0.2s ease,
              background-color 0.2s ease,
              box-shadow 0.2s ease;
          }

          .fdz-drop-area:hover {
            border-color: #3454d1;
            background: #f5f7ff;
          }

          .fdz-drop-area.fdz-drag-active {
            border-color: #3454d1;
            background: #eef2ff;
            box-shadow: 0 0 0 4px rgba(52, 84, 209, 0.1);
          }

          .fdz-hidden-input {
            display: none;
          }

          .fdz-placeholder {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 6px;
            text-align: center;
          }

          .fdz-upload-icon {
            width: 46px;
            height: 46px;
            margin-bottom: 4px;
            border-radius: 50%;
            background: #e9edff;
            color: #3454d1;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 25px;
            font-weight: 700;
            line-height: 1;
          }

          .fdz-placeholder strong {
            color: #252a34;
            font-size: 15px;
          }

          .fdz-placeholder span {
            color: #7a8290;
            font-size: 13px;
          }

          .fdz-required-text {
            color: #dc3545 !important;
            font-size: 12px !important;
          }

          .fdz-file-list {
            display: flex;
            flex-direction: column;
            gap: 10px;
            margin-top: 12px;
          }

          .fdz-file-item {
            width: 100%;
            min-height: 68px;
            padding: 10px 12px;
            border: 1px solid #dfe3e9;
            border-radius: 9px;
            background: #ffffff;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            box-sizing: border-box;
            transition:
              border-color 0.2s ease,
              box-shadow 0.2s ease;
          }

          .fdz-file-item:hover {
            border-color: #b9c3d4;
            box-shadow: 0 3px 10px rgba(20, 30, 50, 0.06);
          }

          .fdz-file-link {
            flex: 1;
            min-width: 0;
            display: flex;
            align-items: center;
            gap: 12px;
            color: #252a34;
            text-decoration: none;
          }

          .fdz-file-link:hover {
            color: #3454d1;
            text-decoration: none;
          }

          .fdz-file-icon {
            position: relative;
            flex-shrink: 0;
            width: 42px;
            height: 48px;
            border-radius: 5px;
            background: #eef1f6;
            display: flex;
            align-items: flex-end;
            justify-content: center;
            padding-bottom: 6px;
            box-sizing: border-box;
          }

          .fdz-file-icon::after {
            content: "";
            position: absolute;
            top: 0;
            right: 0;
            width: 12px;
            height: 12px;
            border-bottom-left-radius: 4px;
            background: #cfd6e3;
          }

          .fdz-file-extension {
            position: relative;
            z-index: 1;
            max-width: 36px;
            color: #526071;
            font-size: 9px;
            font-weight: 700;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .fdz-file-information {
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 3px;
          }

          .fdz-file-name {
            max-width: 100%;
            color: inherit;
            font-size: 14px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .fdz-file-description {
            color: #858d99;
            font-size: 12px;
          }

          .fdz-remove-button {
            flex-shrink: 0;
            width: 34px;
            height: 34px;
            padding: 0;
            border: 0;
            border-radius: 50%;
            background: #fff0f1;
            color: #dc3545;
            cursor: pointer;
            font-size: 22px;
            font-weight: 400;
            line-height: 32px;
            text-align: center;
            transition:
              color 0.2s ease,
              background-color 0.2s ease;
          }

          .fdz-remove-button:hover {
            background: #dc3545;
            color: #ffffff;
          }

          @media (max-width: 576px) {
            .fdz-drop-area {
              min-height: 135px;
              padding: 18px;
            }

            .fdz-file-item {
              align-items: flex-start;
            }

            .fdz-file-description {
              line-height: 1.35;
            }
          }
        `}
      </style>

      <div className="fdz-container">
        <div
          className={`fdz-drop-area ${
            dragActive ? "fdz-drag-active" : ""
          }`}
          role="button"
          tabIndex={0}
          onClick={openFilePicker}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openFilePicker();
            }
          }}
          onDragEnter={(event) => {
            preventDefault(event);
            setDragActive(true);
          }}
          onDragOver={(event) => {
            preventDefault(event);
            setDragActive(true);
          }}
          onDragLeave={(event) => {
            preventDefault(event);

            if (!event.currentTarget.contains(event.relatedTarget)) {
              setDragActive(false);
            }
          }}
          onDrop={(event) => {
            preventDefault(event);
            setDragActive(false);
            addFiles(event.dataTransfer.files);
          }}
        >
          <input
            ref={inputRef}
            type="file"
            className="fdz-hidden-input"
            multiple={multiple}
            accept={accept}
            onChange={(event) => {
              addFiles(event.target.files);
            }}
          />

          <div className="fdz-placeholder">
            <span className="fdz-upload-icon">↑</span>

            <strong>
              {multiple
                ? "Dosyaları buraya sürükleyip bırakın"
                : "Dosyayı buraya sürükleyip bırakın"}
            </strong>

            <span>Dosya seçmek için bu alana tıklayabilirsiniz</span>

            {required && files.length === 0 && (
              <span className="fdz-required-text">
                Bu alan zorunludur
              </span>
            )}
          </div>
        </div>

        {files.length > 0 && (
          <div className="fdz-file-list">
            {files.map((file, index) => (
              <FileItem
                key={`${getFileName(file)}-${index}`}
                file={file}
                onRemove={() => removeFile(index)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default FileDropzone;